import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Tabs, Tab,
  InputAdornment, CircularProgress, Select, MenuItem, FormControl,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { loginStaff, loginStaffByEmail, staffSendOtp, staffVerifyOtp } from '../api/endpoints/auth';
import { getAllowedPaths, getFirstAllowedPath } from '../config/accessControl';
import countryCodes from '../utils/countryCodes';

const Login = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useContext(AuthContext);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Email login
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  // OTP login
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otpId, setOtpId] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  // Unique ID login
  const [name, setName] = useState('');
  const [uniqueId, setUniqueId] = useState('');
  const [password, setPassword] = useState('');

  const handleSuccess = (response) => {
    const result = response.data.data;
    authLogin(result.token, result.user);
    toast.success('Login successful');
    navigate(getFirstAllowedPath(getAllowedPaths(result.user)), { replace: true });
  };

  const handleEmailLogin = async () => {
    if (!email.trim() || !emailPassword) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const data = await loginStaffByEmail(email.trim().toLowerCase(), emailPassword);
      handleSuccess(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const fullPhone = countryCode + phone;
    if (!phone.trim() || phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSendingOtp(true);
    try {
      const res = await staffSendOtp(fullPhone);
      setOtpId(res.data.data.otpId);
      setOtpSent(true);
      setOtpInput('');
      toast.success('OTP sent to your phone');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpInput.trim() || otpInput.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }
    setLoading(true);
    try {
      const data = await staffVerifyOtp(otpId, otpInput.trim());
      handleSuccess(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffLogin = async () => {
    if (!name.trim() || !uniqueId.trim() || !password) {
      toast.error('Please enter name, unique ID, and password');
      return;
    }
    setLoading(true);
    try {
      const data = await loginStaff({ name: name.trim(), uniqueId: uniqueId.trim(), password });
      handleSuccess(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (handler) => (event) => {
    if (event.key === 'Enter') handler();
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #2D1B69 0%, #1A0F3D 100%)', p: 2 }}>
      <Card sx={{ maxWidth: 440, width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <AutoAwesomeIcon sx={{ fontSize: 48, color: '#FFD700', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: '#2D1B69' }}>
              AstroPandit Staff
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sign in with your staff credentials
            </Typography>
          </Box>

          <Tabs value={tab} onChange={(e, v) => { setTab(v); setOtpSent(false); setOtpId(null); }} variant="fullWidth" sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600, fontSize: 13 } }}>
            <Tab label="Email" />
            <Tab label="OTP" />
            <Tab label="Unique ID" />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2.5}>
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown(handleEmailLogin)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="primary" /></InputAdornment> }}
                placeholder="Enter your email"
              />
              <TextField
                label="Password"
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                onKeyDown={handleKeyDown(handleEmailLogin)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary" /></InputAdornment> }}
                placeholder="Enter your password"
              />
              <Button variant="contained" size="large" fullWidth onClick={handleEmailLogin} disabled={loading} sx={{ py: 1.3 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={2.5}>
              {!otpSent ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <FormControl size="small" sx={{ minWidth: 110, mt: 0.5 }}>
                      <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                        {countryCodes.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      onKeyDown={handleKeyDown(handleSendOtp)}
                      fullWidth
                      placeholder="9876543210"
                      InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="primary" /></InputAdornment> }}
                    />
                  </Stack>
                  <Button variant="contained" size="large" fullWidth onClick={handleSendOtp} disabled={sendingOtp} sx={{ py: 1.3 }}>
                    {sendingOtp ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    label="Enter OTP"
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                    onKeyDown={handleKeyDown(handleVerifyOtp)}
                    fullWidth
                    placeholder="6-digit code"
                  />
                  <Button variant="contained" size="large" fullWidth onClick={handleVerifyOtp} disabled={loading} sx={{ py: 1.3 }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
                  </Button>
                  <Button variant="text" size="small" onClick={handleSendOtp} disabled={sendingOtp} sx={{ textTransform: 'none' }}>
                    {sendingOtp ? 'Resending...' : 'Resend OTP'}
                  </Button>
                </>
              )}
            </Stack>
          )}

          {tab === 2 && (
            <Stack spacing={2.5}>
              <TextField
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown(handleStaffLogin)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="primary" /></InputAdornment> }}
                placeholder="Enter your full name"
              />
              <TextField
                label="Unique ID"
                value={uniqueId}
                onChange={(e) => setUniqueId(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown(handleStaffLogin)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon color="primary" /></InputAdornment> }}
                placeholder="Enter your unique ID"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown(handleStaffLogin)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary" /></InputAdornment> }}
                placeholder="Enter your password"
              />
              <Button variant="contained" size="large" fullWidth onClick={handleStaffLogin} disabled={loading} sx={{ py: 1.3 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;